/**
 * Analytics Service
 *
 * Comprehensive service for analytics operations including:
 * - Fetching analytics data
 */

import { httpClient } from './http-client.service';

// Table column definition (matches analytics-portal types)
export interface TableColumn {
  key: string;
  label: string;
}

// Row data is dynamic
export type TableRow = Record<string, string | number | null>;

// Table configuration
export interface TableConfig {
  type: 'table';
  id: string;
  title: string;
  columns: TableColumn[];
  rows: TableRow[];
  totalRows?: number;
}

// Analytics Portal Table Component Props
export interface AnalyticsPortalTableProps {
  title: string;
  columns: TableColumn[];
  data: TableRow[];
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading: boolean;
  dropdownZIndex?: number;
}

export interface CardConfig {
  type: 'card';
  id: string;
  title: string;
  formatType: string;
  value: number;
  comparison?: string;
  clickable?: boolean;
}

export interface MiniCardConfig {
  type: 'mini_card';
  id: string;
  title: string;
  value: number;
  clickable?: boolean;
}

export interface ButtonConfig {
  id: string;
  title: string;
}

export interface ChartDataItem {
  name: string;
  value: number;
}

export interface ChartConfig {
  type: 'pie_chart' | 'bar_chart';
  id: string;
  overall_title: string;
  overall_color: string,
  colors: string[];
  icon: string;
  data: ChartDataItem[];
  hasButtons: boolean;
  buttons: ButtonConfig[];
}

export interface QueryExecuteResponse {
  status: number | string;
  message?: string;
  data: {
    query: string;
    columns: Array<{ key: string; label: string }>;
    rows: Array<Record<string, string | null>>;
    totalRows: number;
  };
}

export interface DirectSearchResponse {
  status: number | string;
  message?: string;
  data: RowConfig[];
}

// Card or other item types (can be extended later)
export type ItemConfig = TableConfig | CardConfig | MiniCardConfig | ChartConfig;

export interface RowConfig {
  type: 'row';
  items: ItemConfig[];
}

export interface AnalyticsResponse {
  rows?: RowConfig[];
  tables?: TableConfig[];
}

// Backend API response wrapper
interface AnalyticsApiResponse {
  status: number | string;
  ver?: string;
  message?: string;
  data: {
    rows?: RowConfig[];
    items?: RowConfig[];
    tables?: Array<{
      type: 'table';
      id: string;
      title: string;
      columns: Array<{
        key: string;
        label: string;
      }>;
      rows: Array<Record<string, string | null>>;
      totalRows?: number;
    }>;
  };
  statusCode?: number;
}

export const analyticsService = {
  /**
   * Fetch analytics data from backend (initial load)
   * Returns config-driven response with rows/items structure
   */
  async fetchAnalytics(): Promise<AnalyticsResponse> {
    try {
      const response = await httpClient.post<AnalyticsApiResponse>('pfm/UserAnalytics', {});

      const backendData = (response.data as any).data;

      // Check if backend returned array of RowConfig directly
      if (Array.isArray(backendData) && backendData.length > 0 && backendData[0].type === 'row') {
        const result: AnalyticsResponse = {
          rows: backendData,
          tables: undefined
        };
        return result;
      }

      const result: AnalyticsResponse = {
        rows: backendData?.rows || backendData?.items,
        tables: backendData?.tables
      };
      return result;
    } catch (error) {
      console.error('[analyticsService] Error fetching analytics:', error);
      throw error;
    }
  },

  /**
   * Fetch a single row by index (progressive loading)
   * Sends { row_index: rowIndex } to the endpoint, returns a single RowConfig
   */
  async fetchAnalyticsRow(rowIndex: number): Promise<RowConfig | null> {
    try {
      const response = await httpClient.post<AnalyticsApiResponse>('pfm/UserAnalytics', {
        row_index: rowIndex
      });
      const backendData = (response.data as any).data;
      if (Array.isArray(backendData) && backendData.length > 0 && backendData[0].type === 'row') {
        return backendData[0];
      }
      return null;
    } catch (error) {
      console.error(`[analyticsService] Error fetching analytics row ${rowIndex}:`, error);
      throw error;
    }
  },

  /**
   * Fetch a specific page of table data (for pagination)
   */
  async fetchTablePage(tableId: string, page: number, pageSize: number): Promise<{
    rows: TableRow[];
    columns: TableColumn[];
    currentPage: number;
    pageSize: number;
    totalRows?: number;
    totalPages?: number;
  }> {
    try {
      const response = await httpClient.post<AnalyticsApiResponse>('pfm/UserAnalytics', {
        table_page_request: {
          id: tableId,
          page,
          pageSize
        }
      });

      const rowData = (response.data as any).data;

      if (!rowData || !Array.isArray(rowData) || rowData.length === 0) {
        throw new Error('No row data in response');
      }

      const firstRow = rowData[0];
      if (!firstRow.items || firstRow.items.length === 0) {
        throw new Error('No items in first row');
      }

      const tableItem = firstRow.items.find((item: any) => item.type === 'table') as TableConfig;
      if (!tableItem) {
        throw new Error('No table data in response');
      }

      console.log('[analyticsService] fetchTablePage extracted table:', {
        rowsCount: tableItem.rows?.length,
        columnsCount: tableItem.columns?.length,
        totalRows: tableItem.totalRows
      });

      return {
        rows: tableItem.rows || [],
        columns: tableItem.columns || [],
        currentPage: page,
        pageSize: pageSize,
        totalRows: tableItem.totalRows,  
        totalPages: tableItem.totalRows ? Math.ceil(tableItem.totalRows / pageSize) : undefined
      };
    } catch (error) {
      console.error('[analyticsService] fetchTablePage error:', error);
      throw error;
    }
  },

  /**
   * Search with NLP or direct query - single unified endpoint
   * Backend handles NLP conversion and returns query + results
   */
  async querySearch(message: string, chatId: string, page: number = 1, pageSize: number = 10): Promise<QueryExecuteResponse> {
    try {
      console.log('[analyticsService] querySearch called with:', { message, chatId, page, pageSize });
      const response = await httpClient.post<any>('pfm/UserAnalytics', {
        query_search_request: {
          message,
          page,
          pageSize
        }
      });

      const result: QueryExecuteResponse = {
        status: response.data.status,
        message: response.data.message,
        data: response.data.data
      };

      return result;
    } catch (error) {
      console.error('[analyticsService] querySearch error:', error);
      throw error;
    }
  },

  /**
   * Direct search - for "Search" dropdown mode
   * Sends direct_search_request to backend
   * Returns data in RowConfig[] format (similar to fetchAnalytics)
   */
  async directSearch(message: string): Promise<DirectSearchResponse> {
    try {
      console.log('[analyticsService] directSearch called with:', { message });
      const response = await httpClient.post<any>('pfm/UserAnalytics', {
        direct_search_request: {
          message
        }
      });

      const result: DirectSearchResponse = {
        status: response.data.status,
        message: response.data.message,
        data: response.data.data
      };

      return result;
    } catch (error) {
      console.error('[analyticsService] directSearch error:', error);
      throw error;
    }
  },

  /**
   * Fetch card detail rows (drill-down into a card)
   */
  async fetchCardDetail(cardId: string, page: number = 1, pageSize: number = 10): Promise<{
    columns: TableColumn[];
    rows: TableRow[];
    totalRows: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
    title: string;
  }> {
    try {
      const response = await httpClient.post<any>('pfm/UserAnalytics', {
        card_detail_request: { id: cardId, page, pageSize }
      });

      const data = response.data?.data;
      return {
        columns: data?.columns || [],
        rows: data?.rows || [],
        totalRows: data?.totalRows || 0,
        currentPage: data?.currentPage || page,
        pageSize: data?.pageSize || pageSize,
        totalPages: data?.totalPages || 0,
        title: data?.title || cardId,
      };
    } catch (error) {
      console.error('[analyticsService] fetchCardDetail error:', error);
      throw error;
    }
  },

  /**
   * Download full card detail as CSV
   */
  async downloadCardReport(cardId: string): Promise<{ csv: string; filename: string }> {
    try {
      const response = await httpClient.post<any>('pfm/UserAnalytics', {
        card_detail_download: { id: cardId }
      });

      const data = response.data?.data;
      return {
        csv: data?.csv || '',
        filename: data?.filename || `${cardId}_report.csv`,
      };
    } catch (error) {
      console.error('[analyticsService] downloadCardReport error:', error);
      throw error;
    }
  },

  /**
   * Fetch chart data for a specific chart
   * Sends chart_query_request with chart id to backend
   * Returns updated chart data in RowConfig format
   */
  async fetchChartData(chartId: string): Promise<{
    status: number | string;
    message?: string;
    data: RowConfig[];
  }> {
    try {
      console.log('[analyticsService] fetchChartData called with:', { chartId });
      const response = await httpClient.post<any>('pfm/UserAnalytics', {
        chart_query_request: {
          id: chartId
        }
      });

      return {
        status: response.data.status,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error) {
      console.error('[analyticsService] fetchChartData error:', error);
      throw error;
    }
  },

};

export function formatValue(value: number, formatType: string): string {
  switch (formatType) {
    case 'currency':
      return `₹${value.toLocaleString()}`;
    case 'percentage':
      return `${value.toFixed(1)}%`;
    default:
      return value.toLocaleString();
  }
}

export default analyticsService;
